import React, { useEffect } from "react";
import { Image, Stack } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { TournamentEntry } from "@nestcoinco/hyper-api-gateway-api-client";
import { useAtomValue } from "jotai";

import "./DeclineChallengeGameModal.scss";
import GhostButton from "../../dashboard-page/GhostButton/GhostButton";
import BottomDrawer from "../../BottomDrawer/BottomDrawer";
import HyperCoin from "../../HyperCoin/HyperCoin";
import ImageRedX from "../../../images/red-x-rounded.svg";
import useAuth from "../../../hooks/useAuth";
import FilledButton, {
  ButtonSizes,
} from "../../dashboard-page/FilledButton/FilledButton";
import { CustomSpinner } from "../../CustomSpinner/CustomSpinner";
import { GenericModalProps } from "../../../utils/constants/generic-types";
import { GameType } from "../../../utils/constants";
import useAnalytics from "../../../hooks/useAnalytics";
import WarningBadge from "../../WarningBadge/WarningBadge";
import useCancelTournamentEntryMutation from "../../../react-query/mutations/use-cancel-tournament-entry";
import { userCustomAvatarAtom } from "../../../jotai/atoms/user";

interface DeclineChallengeGameModalProps
  extends GenericModalProps, TournamentEntry {
  gameType: GameType;
  refetch?: () => void;
}

function DeclineChallengeGameModal({
  isOpen,
  onClose,
  gameType,
  refetch,
  ...props
}: DeclineChallengeGameModalProps) {
  const { t } = useTranslation();
  const analytics = useAnalytics();
  const { userProfile } = useAuth();
  const userCustomAvatar = useAtomValue(userCustomAvatarAtom);
  const {
    id: entryId,
    participants,
    tournament: {
      cancelPenalty,
      stakeAmount,
      game: { name: gameName },
    },
  } = props;
  const challenger = participants?.find(
    (participant) => participant.username !== userProfile?.username
  );
  const {
    cancelEntry,
    mutation: { isPending, isSuccess },
  } = useCancelTournamentEntryMutation({
    entryId,
  });

  useEffect(() => {
    if (!isSuccess) return;
    analytics.sendCancelGameEvent({
      entry_fee: stakeAmount,
      game_name: gameName,
      game_mode: gameType,
    });
    refetch && refetch();
  }, [analytics, isSuccess, gameName, gameType, stakeAmount, refetch]);

  return (
    <BottomDrawer
      isOpen={isOpen}
      onClose={onClose}
      containerClass="decline-challenge-game-modal"
    >
      <Stack gap={4}>
        <div className="avatars pt-2">
          <div className="avatar-rounded-wrapper">
            <Image
              alt={userProfile?.avatar?.label}
              src={userCustomAvatar || userProfile?.avatar?.url}
            />
          </div>
          <div
            className="avatar-rounded-wrapper"
            style={{
              left: -24,
              background: "#C6EFEA",
            }}
          >
            <Image alt="" src={challenger?.avatarUrl} />
          </div>
          <Image
            alt=""
            src={ImageRedX}
            className="red-x"
            width={24}
            height={24}
          />
        </div>

        <div className="d-flex flex-column text-content-section">
          <div className="has-already-played d-flex align-items-center">
            <span className="img-wrapper">
              <Image alt="" src={challenger?.avatarUrl} />
            </span>
            <span className="text">
              {t("challenger_has_already_played", {
                challenger: `@${challenger?.username}`,
              })}
            </span>
          </div>
          <h2 className="heading-text">{t("decline_game_request")}</h2>
          <p className="subhead-text">
            {t("are_you_sure_you_want_to_decline_this_game_request_from")}{" "}
            <span className="fw-bold">@{challenger?.username}</span>?
          </p>
          <WarningBadge
            text={t(
              "avoid_penalties_by_only_creating_games_you_want_to_play_with_friends"
            )}
            containerClassName="avoid-penalties-warning-text avoid-penalties-warning"
            iconSize={16}
          />
        </div>

        <Stack>
          <FilledButton
            fullwidth
            size={ButtonSizes.MD}
            className="text-capitalize"
            onClick={onClose}
            disabled={isPending}
          >
            {t("no_go_back")}
          </FilledButton>
          <GhostButton
            className="decline-btn"
            onClick={cancelEntry}
            disabled={isPending}
          >
            <span className="d-flex align-items-center gap-1">
              <span>{t("decline_game")}</span>
              <span>
                <HyperCoin /> -{cancelPenalty}
              </span>
            </span>

            {isPending && <CustomSpinner size="sm" className="ms-1" />}
          </GhostButton>
        </Stack>
      </Stack>
    </BottomDrawer>
  );
}

export default DeclineChallengeGameModal;
